// Plan / credits denials and the app QueryClient that re-reads GET /entitlements after
// one (simple-mode S4 4.2). Deliberately free of SessionContext so the test utilities and
// App can import it without the session module.
import { MutationCache, QueryCache, QueryClient, type QueryClientConfig } from '@tanstack/react-query'
import { isApiError, type ApiError } from '../api/client'

/**
 * Used only when the server sent no remedy for a key (an older gateway, or a key the
 * registry does not know). The real copy always comes from `features[key].remedy` or
 * from the denial's own `remedy`.
 */
export const FALLBACK_REMEDY = 'Upgrade your plan to use this feature.'

/** 403 plan_restricted (the `X-Entitlement-Denied` header, or the body's type). */
export function isPlanRestricted(err: unknown): err is ApiError {
  return isApiError(err) && err.status === 403 && (!!err.entitlement_denied || err.type === 'plan_restricted')
}

/** 402 credits_exhausted (a Free workspace out of one-time credits, D49). */
export function isCreditsExhausted(err: unknown): err is ApiError {
  return isApiError(err) && err.status === 402 && (err.code === 'credits_exhausted' || err.type === 'credits_exhausted')
}

/** Any denial that means our cached entitlements are stale. */
export function isEntitlementDenial(err: unknown): err is ApiError {
  return isPlanRestricted(err) || isCreditsExhausted(err)
}

/** Re-read entitlements (every workspace's cached copy) after a denial. */
export function refreshOnEntitlementDenial(qc: QueryClient, err: unknown): boolean {
  if (!isEntitlementDenial(err)) return false
  void qc.invalidateQueries({ queryKey: ['entitlements'] })
  return true
}

/**
 * The app's QueryClient: any failed mutation or query (other than the entitlements read
 * itself) that is a plan / credits denial re-reads GET /entitlements once (S4 4.2).
 * Views that swallow an error inside their mutationFn (the Playground's run history)
 * call `refreshOnEntitlementDenial` themselves.
 */
export function createAppQueryClient(config: QueryClientConfig = {}): QueryClient {
  let qc: QueryClient | null = null
  const onError = (err: unknown) => {
    if (qc) refreshOnEntitlementDenial(qc, err)
  }
  qc = new QueryClient({
    ...config,
    queryCache: new QueryCache({
      onError: (err, query) => {
        if (query.queryKey[0] !== 'entitlements') onError(err)
      },
    }),
    mutationCache: new MutationCache({ onError }),
  })
  return qc
}

